import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

/** What a non-production deployment calls itself: the name in the strip above the app bar. */
type DeploymentKind = "development" | "staging" | "test"

const KINDS: DeploymentKind[] = ["development", "staging", "test"]

// APP_BANNER as docker/40-lewara-runtime-config.sh wrote it. Unset is production. Any value outside
// KINDS still asked to be marked, so it gets the generic name.
function kindOf(value: string | undefined): DeploymentKind | null {
  const banner = (value ?? "").trim().toLowerCase()
  if (banner === "" || banner === "off" || banner === "none") return null
  return KINDS.find(kind => kind === banner) ?? "test"
}

/**
 * Null on production. Otherwise the deployment is marked twice, so nobody works in it by mistake: a
 * gold strip above the app bar on every page (LewDeploymentBanner), and the browser tab, whose title
 * starts with the short name and whose diamond turns gold (useDocumentTitle, markFavicon). The tab is
 * what tells a test tab from a production one when both are open side by side. Staging sets it while
 * running as `production` in every other respect.
 *
 * Constant: /config.js is read once, before the app. Never in the Erklärung's frame, which explains
 * the app, not the deployment it happens to run on.
 */
export const DEPLOYMENT_KIND: DeploymentKind | null = IS_VIEW_HELP_EMBED
  ? null
  : kindOf(window.__LEWARA_CONFIG__?.banner)

// The logo's two blues and the golds that replace them on the tab icon: light half, dark half.
const FAVICON_GOLD: Record<string, string> = { "#65ccea": "#F1CB8E", "#20abe3": "#D9922E" }
const FAVICON_SIZE = 64

/**
 * Replaces the tab icon with the same diamond in gold. A recoloured mark rather than a dot on the
 * corner, which reads as an unread notification. Drawn from the logo SVG rather than from
 * /favicon.ico: browsers will not decode that ICO into an image, and the SVG is the same diamond,
 * sharp at any size. Sized before drawing because an SVG without width and height has no size to draw
 * at in Firefox, and rasterised because Safari will not take an SVG favicon.
 *
 * A failed redraw leaves the plain icon; the strip and the tab title still say what this is. Hence
 * the swallowed fetch error: it must not reach Sentry as an unhandled rejection.
 */
export async function markFavicon() {
  const link = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
  if (!link) return

  const response = await fetch(new URL("@/assets/logo_icon.svg", import.meta.url).href).catch(() => null)
  if (!response?.ok) return
  const svg = (await response.text())
    .replace(/#65ccea|#20abe3/gi, blue => FAVICON_GOLD[blue.toLowerCase()])
    .replace("<svg ", `<svg width="${FAVICON_SIZE}" height="${FAVICON_SIZE}" `)

  const icon = new Image()
  icon.onload = () => {
    const canvas = document.createElement("canvas")
    canvas.width = canvas.height = FAVICON_SIZE
    const context = canvas.getContext("2d")
    if (!context) return

    context.drawImage(icon, 0, 0, FAVICON_SIZE, FAVICON_SIZE)
    link.href = canvas.toDataURL("image/png")
  }
  icon.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}
